import { useState, useEffect } from "react";
import { getFilterState, saveFilterState } from "../utils/filterUtils";
import { log } from "../Helper";

/**
 * Custom hook for managing table column filter states with persistent storage
 * 
 * PURPOSE:
 * - Manages filter values for individual table columns (search boxes in column headers)
 * - Persists filter states in localStorage across browser sessions
 * - Automatically clears session storage on page refresh to prevent conflicts
 * 
 * DIFFERENCE FROM useComarFilter:
 * - useComarFilter: Handles navigation-based filtering from urgent action tiles
 * - useFilterState: Handles column-level filtering within tables (search boxes)
 * 
 * USAGE:
 * - Used in table components that have column-based filtering
 * - Pass unique table name and initial column configuration
 * - Returns updated columns with filter values and change handler
 * 
 * @param {string} tableName - Unique identifier for the table (used as localStorage key)
 * @param {Array} initialColumns - Initial column configuration with filter settings
 * @returns {Array} [columns, handleFilterChange] - Updated columns and change handler
 */
const useFilterState = (tableName, initialColumns) => {
  // State to store the column configurations including their filter values
  // Each column object contains field info, display settings, and current filter value
  const [columns, setColumns] = useState(initialColumns);

  // SESSION STORAGE CLEANUP:
  // Clears session storage on page refresh to prevent filter conflicts
  // This ensures fresh filter state on each page load
  useEffect(() => {
    const handleBeforeUnload = () => {
      sessionStorage.clear(); // Clear temporary session data
    };

    // Register event listener for page unload
    window.addEventListener("beforeunload", handleBeforeUnload);

    // Cleanup function to remove the event listener when component unmounts
    return () => {
      window.removeEventListener("beforeunload", handleBeforeUnload);
    };
  }, []); // Empty dependency array - runs once on mount

  // FILTER STATE RESTORATION:
  // Loads previously saved filter values from localStorage when component mounts
  // This maintains user's filter preferences across browser sessions
  useEffect(() => {
    // Retrieve the saved filter values for this specific table from localStorage
    const savedFilters = getFilterState(tableName);
    
    // Update each column's filter value with the saved state
    // If no saved value exists for a column, default to empty string
    setColumns((prevColumns) =>
      prevColumns.map((col) => ({
        ...col, // Preserve all existing column properties
        tableData: {
          ...col.tableData, // Preserve existing tableData
          filterValue: savedFilters[col.field] || "", // Set saved filter or empty string
        },
      }))
    );
  }, [tableName]); // Re-run when tableName changes (different table)

  // FILTER CHANGE HANDLER:
  // Updates filter value for a specific column and persists it to localStorage
  // Called when user types in column filter input boxes
  const handleFilterChange = (columnId, filterValue) => {
    // STATE UPDATE: Update the specific column's filter value in component state
    setColumns((prevColumns) =>
      prevColumns.map((col, index) =>
        index === columnId
          ? {
              ...col, // Preserve all existing column properties
              tableData: {
                ...col.tableData, // Preserve existing tableData
                filterValue: filterValue, // Update only the filter value
              },
            }
          : col // Return unchanged if not the target column
      )
    );

    // PERSISTENCE: Save the updated filter state to localStorage
    // This ensures filter values persist across browser sessions
    
    // Get current saved filters for this table
    const savedFilters = getFilterState(tableName);

    // Update the filter state with the new value for the specific column
    savedFilters[columns[columnId].field] = filterValue;

    // Save the updated filter state back to localStorage
    saveFilterState(tableName, savedFilters);
  };

  // HOOK RETURN VALUES:
  // Returns the updated column configurations and the change handler function
  // These are used by table components to manage column filtering
  return [columns, handleFilterChange];
};

export default useFilterState;
